/**
 * SYM-5391. Which of the two video buttons is blocked on a vacancy, and how open tabs tell
 * each other. The server holds one lock per vacancy per provider; the API reports them as
 * `video_generation_locks: { creatomate, fal }` on every vacancy it returns. The server's 409 on
 * a duplicate request stays as the fail-safe: hiding a button is interface behaviour, never
 * protection (see sympl-app-context/rules/permissions.md).
 */

export type VideoLockProvider = 'creatomate' | 'fal';

export type VideoGenerationLocks = Record<VideoLockProvider, boolean>;

export const NO_VIDEO_LOCKS: VideoGenerationLocks = {
  creatomate: false,
  fal: false,
};

/** One message on the cross-tab channel: a provider's lock changed on a vacancy. */
export type VideoLockMessage = {
  vacancyId: number;
  provider: VideoLockProvider;
  locked: boolean;
};

export const VIDEO_LOCK_CHANNEL = 'sympl-video-generation-locks';

/** Shown where a hidden button used to be. */
export const VIDEO_LOCKED_MESSAGE =
  'A video is being generated for this campaign. This comes back as soon as it is done.';

/** Reads the API's shape defensively: anything missing counts as unlocked. */
export const readVideoLocks = (raw: unknown): VideoGenerationLocks => {
  const value = (raw ?? {}) as Partial<Record<string, unknown>>;
  return {
    creatomate: value.creatomate === true,
    fal: value.fal === true,
  };
};

/** Applies a channel message to the locks of ONE vacancy; other vacancies' messages are ignored. */
export const applyVideoLockMessage = (
  locks: VideoGenerationLocks,
  message: VideoLockMessage,
  vacancyId: number | null | undefined
): VideoGenerationLocks => {
  if (!vacancyId || message.vacancyId !== vacancyId) return locks;
  if (locks[message.provider] === message.locked) return locks;
  return { ...locks, [message.provider]: message.locked };
};

export const isVideoLockMessage = (data: unknown): data is VideoLockMessage => {
  if (!data || typeof data !== 'object') return false;
  const m = data as Record<string, unknown>;
  return (
    typeof m.vacancyId === 'number' &&
    (m.provider === 'creatomate' || m.provider === 'fal') &&
    typeof m.locked === 'boolean'
  );
};

/**
 * The tab-to-tab channel, or null where the browser has none. Every consumer must cope with
 * null: the feature then degrades to "refetch when the modal opens", which is the stale guard.
 */
export const openVideoLockChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(VIDEO_LOCK_CHANNEL);
  } catch {
    return null;
  }
};
